// frontend/src/components/Auth/Register.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';

const RESEND_COOLDOWN = 60; // seconds

const Register = () => {
  const navigate = useNavigate();

  // Step: 'form' | 'otp' | 'done'
  const [step, setStep] = useState('form');

  // Form fields
  const [formData, setFormData] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // OTP state
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMsg, setResendMsg] = useState('');
  const otpRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];
  const cooldownTimer = useRef(null);

  useEffect(() => {
    return () => clearInterval(cooldownTimer.current);
  }, []);

  const startCooldown = () => {
    setResendCooldown(RESEND_COOLDOWN);
    clearInterval(cooldownTimer.current);
    cooldownTimer.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownTimer.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleChange = e => setFormData(f => ({ ...f, [e.target.name]: e.target.value }));

  // ── Step 1: validate form + send OTP ──────────────────────────────────────
  const handleSendOtp = async e => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) return setError('Passwords do not match');
    if (formData.password.length < 6) return setError('Password must be at least 6 characters');

    setLoading(true);
    try {
      await api.post('/auth/register/send-otp', { name: formData.name, email: formData.email });
      setStep('otp');
      startCooldown();
      setTimeout(() => otpRefs[0].current?.focus(), 100);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── OTP input handling ─────────────────────────────────────────────────────
  const handleOtpChange = (idx, val) => {
    const digits = val.replace(/\D/g, '').slice(0, 1);
    const next = [...otpDigits];
    next[idx] = digits;
    setOtpDigits(next);
    setOtpError('');
    if (digits && idx < 5) otpRefs[idx + 1].current?.focus();
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !otpDigits[idx] && idx > 0) {
      otpRefs[idx - 1].current?.focus();
    }
    if (e.key === 'ArrowLeft' && idx > 0) otpRefs[idx - 1].current?.focus();
    if (e.key === 'ArrowRight' && idx < 5) otpRefs[idx + 1].current?.focus();
  };

  const handleOtpPaste = e => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = [...otpDigits];
    for (let i = 0; i < 6; i++) next[i] = pasted[i] || '';
    setOtpDigits(next);
    if (pasted.length >= 6) otpRefs[5].current?.focus();
    else otpRefs[Math.min(pasted.length, 5)].current?.focus();
  };

  // ── Resend OTP ─────────────────────────────────────────────────────────────
  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setResendMsg('');
    setOtpError('');
    try {
      await api.post('/auth/register/send-otp', { name: formData.name, email: formData.email });
      setResendMsg('OTP resent! Check your inbox (same OTP if still valid).');
      startCooldown();
    } catch (err) {
      setOtpError(err.response?.data?.error || 'Failed to resend OTP');
    }
  };

  // ── Step 2: Verify OTP + Complete Registration ─────────────────────────────
  const handleVerifyOtp = async e => {
    e.preventDefault();
    setOtpError('');
    const otp = otpDigits.join('');
    if (otp.length < 6) return setOtpError('Enter all 6 digits');

    setOtpLoading(true);
    try {
      await api.post('/auth/register/verify', {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        otp
      });
      setStep('done');
    } catch (err) {
      setOtpError(err.response?.data?.error || 'Verification failed. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // ────────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 py-12 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">

        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex justify-center mb-3">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-full p-3">
              <svg className="h-10 w-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-extrabold text-gray-900">Register Your Company</h2>

          {/* Step indicator */}
          {step !== 'done' && (
            <div className="flex items-center justify-center space-x-2 mt-3">
              <div className={`flex items-center space-x-1.5 ${step === 'form' ? 'text-purple-600' : 'text-green-600'}`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step === 'form' ? 'bg-purple-600 text-white' : 'bg-green-500 text-white'}`}>
                  {step === 'form' ? '1' : '✓'}
                </span>
                <span className="text-xs font-medium">Details</span>
              </div>
              <div className={`w-8 h-px ${step === 'otp' ? 'bg-purple-400' : 'bg-gray-300'}`} />
              <div className={`flex items-center space-x-1.5 ${step === 'otp' ? 'text-purple-600' : 'text-gray-400'}`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step === 'otp' ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  2
                </span>
                <span className="text-xs font-medium">Verify Email</span>
              </div>
            </div>
          )}
        </div>

        {/* ── STEP 1: Registration Form ─────────────────────────────────── */}
        {step === 'form' && (
          <form onSubmit={handleSendOtp} className="space-y-4">
            {error && (
              <div className="bg-red-50 border-l-4 border-red-400 p-3 rounded-md">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
              <input name="name" type="text" required value={formData.name} onChange={handleChange}
                placeholder="Acme Inc."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Email</label>
              <input name="email" type="email" required value={formData.email} onChange={handleChange}
                placeholder="admin@yourcompany.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all" />
              <p className="mt-1 text-xs text-gray-400">Must be a company email — personal addresses (Gmail, Yahoo, etc.) are not accepted</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input name="password" type="password" required value={formData.password} onChange={handleChange}
                placeholder="••••••••"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all" />
              <p className="mt-1 text-xs text-gray-500">Minimum 6 characters</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <input name="confirmPassword" type="password" required value={formData.confirmPassword} onChange={handleChange}
                placeholder="••••••••"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all" />
            </div>

            <div className="flex items-center">
              <input id="terms" type="checkbox" required
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded" />
              <label htmlFor="terms" className="ml-2 text-sm text-gray-700">I agree to the Terms and Conditions</label>
            </div>

            <button type="submit" disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-medium rounded-lg shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center">
              {loading ? (
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : (
                <>Send Verification OTP →</>
              )}
            </button>

            <p className="text-center text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-purple-600 hover:text-purple-500">Sign in</Link>
            </p>
          </form>
        )}

        {/* ── STEP 2: OTP Entry ─────────────────────────────────────────── */}
        {step === 'otp' && (
          <form onSubmit={handleVerifyOtp} className="space-y-5">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
              <p className="font-semibold mb-1">📧 Check Your Email</p>
              <p>We sent a 6-digit OTP to <strong>{formData.email}</strong>.</p>
              <p className="text-xs mt-1 text-blue-600">Valid for 1 hour. Resend will deliver the same OTP if still active.</p>
            </div>

            {otpError && (
              <div className="bg-red-50 border-l-4 border-red-400 p-3 rounded-md">
                <p className="text-sm text-red-700">{otpError}</p>
              </div>
            )}
            {resendMsg && (
              <div className="bg-green-50 border-l-4 border-green-400 p-3 rounded-md">
                <p className="text-sm text-green-700">{resendMsg}</p>
              </div>
            )}

            {/* 6 OTP digit boxes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3 text-center">Enter OTP</label>
              <div className="flex justify-center space-x-3" onPaste={handleOtpPaste}>
                {otpDigits.map((d, i) => (
                  <input
                    key={i}
                    ref={otpRefs[i]}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    onChange={e => handleOtpChange(i, e.target.value)}
                    onKeyDown={e => handleOtpKeyDown(i, e)}
                    className={`w-12 h-14 text-center text-2xl font-bold border-2 rounded-xl transition-all focus:outline-none
                      ${d ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-300 bg-white text-gray-900'}
                      focus:border-purple-500 focus:ring-2 focus:ring-purple-200`}
                  />
                ))}
              </div>
            </div>

            <button type="submit" disabled={otpLoading || otpDigits.join('').length < 6}
              className="w-full py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-medium rounded-lg shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center">
              {otpLoading ? (
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : (
                <>✓ Verify &amp; Complete Registration</>
              )}
            </button>

            {/* Resend + Back */}
            <div className="flex items-center justify-between text-sm">
              <button type="button" onClick={() => { setStep('form'); setOtpDigits(['','','','','','']); setOtpError(''); }}
                className="text-gray-500 hover:text-gray-700">
                ← Change details
              </button>
              <button type="button" onClick={handleResend} disabled={resendCooldown > 0}
                className="text-purple-600 hover:text-purple-500 disabled:text-gray-400 disabled:cursor-not-allowed font-medium">
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
              </button>
            </div>
          </form>
        )}

        {/* ── STEP 3: Success ───────────────────────────────────────────── */}
        {step === 'done' && (
          <div className="text-center space-y-5">
            <div className="flex justify-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-800">Registration Successful!</h3>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-left">
              <p className="font-semibold text-amber-800 mb-2">⏳ Account Pending Approval</p>
              <p className="text-sm text-amber-700">
                Your email has been verified. Your account is now under review by the administrator.
              </p>
              <p className="text-sm text-amber-700 mt-2">
                You will receive a confirmation email once your account is activated — typically within <strong>24 hours</strong>.
              </p>
            </div>
            <button onClick={() => navigate('/login')}
              className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-medium rounded-lg shadow-lg transition-all">
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Register;
